import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import { UserPicker } from '@/components/UserPicker/UserPicker';
import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  RequestWithRelations,
  RequestWithRelationsFromRequestsList,
  useUpdateRequestSupervisor,
} from '@/models/request';
import { hasPermission, useUsers } from '@/models/user';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';
import useDebouncedState from '@/utils/useDebouncedState';

type SupervisorPickerRequest = Pick<
  RequestWithRelations | RequestWithRelationsFromRequestsList,
  'id' | 'reference' | 'supervisor' | 'allArticles' | 'afterSalesId'
>;

export const SupervisorPicker = ({
  request,
  disabled: disabledProp,
  variant,
}: {
  request: SupervisorPickerRequest;
  disabled?: boolean;
  variant: 'card' | 'row' | 'sidebar';
}) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const [organization] = useCurrentOrganization();

  const workshopIds = request.allArticles
    .map((article) => article.workshopId)
    .filter(Boolean) as string[];

  const hasSupervisePermission = !!hasPermission(currentSession, 'supervise_request', [
    {
      organizationId: organization?.id,
      entityId: null,
    },
    {
      entityId: request.afterSalesId,
    },
    ...workshopIds.map((workshopId) => ({ entityId: workshopId })),
  ]);

  const disabled = disabledProp || !hasSupervisePermission;

  if (variant === 'row') {
    return <SupervisorPickerContent request={request} disabled={disabled} variant={variant} />;
  }

  if (variant === 'card') {
    if (disabled && !request.supervisor) {
      return null;
    }

    return (
      <Box padding="0.25rem 0.5rem" style={{ flex: 'none' }}>
        <SupervisorPickerContent request={request} disabled={disabled} variant={variant} />
      </Box>
    );
  }

  if (variant === 'sidebar') {
    const label = t({ id: 'request.supervisor', message: 'Supervisor' });

    return (
      <Stack gap="0.125rem">
        <Paragraph color="secondary" size="200">
          {label}
        </Paragraph>
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          {(!!request.supervisor || !disabled) && (
            <SupervisorPickerContent request={request} disabled={disabled} variant={variant} />
          )}
          {(!!request.supervisor || disabled) && (
            <Paragraph nowrap ellipsis aria-label={label} className="sentry-mask">
              {request.supervisor?.name ?? '-'}
            </Paragraph>
          )}
        </Stack>
      </Stack>
    );
  }
};

const SupervisorPickerContent = ({
  request,
  disabled,
  variant,
}: {
  request: SupervisorPickerRequest;
  disabled: boolean;
  variant: 'card' | 'row' | 'sidebar';
}) => {
  const { t } = useLingui();

  const [search, debouncedSearch, setSearch] = useDebouncedState('');

  const { data: { users } = { users: [] }, isFetching: isFetchingUsers } = useUsers(
    {
      permissions: ['supervise_request'],
      search: debouncedSearch,
    },
    {
      enabled: !disabled,
      keepPreviousData: true,
    }
  );

  const { mutate: updateRequestSupervisor } = useUpdateRequestSupervisor(request.id);

  const size = variant === 'row' ? 'medium' : variant === 'sidebar' ? 'small' : 'xx-small';

  if (disabled) {
    if (!request.supervisor && variant !== 'row') {
      return <InlineText weight="medium">-</InlineText>;
    }

    return (
      <UserAvatar
        user={request.supervisor}
        size={size}
        tooltip={
          request.supervisor ? <p className="sentry-mask">{request.supervisor.name}</p> : undefined
        }
        disabled={disabled}
      />
    );
  }

  const requestReference = request.reference;
  const requestSupervisorName = request.supervisor?.name;
  const ariaLabel = request.supervisor
    ? t(
        msg({
          id: 'requests.supervisor-picker.label.assigned',
          message: `Request ${requestReference} is supervised by ${requestSupervisorName} - Edit`,
        })
      )
    : t(
        msg({
          id: 'requests.supervisor-picker.label.unassigned',
          message: `Request ${requestReference} isn't supervised - Edit`,
        })
      );

  const tooltipAddLabel = t({
    id: 'request.supervisor-picker.tooltip.add',
    message: 'Add a supervisor',
  });

  const tooltipChangeLabel = t({
    id: 'request.supervisor-picker.tooltip.change',
    message: 'Change supervisor',
  });

  return (
    <UserPicker
      size={size}
      ariaLabel={ariaLabel}
      selectedUser={request.supervisor}
      users={users}
      isLoadingUsers={isFetchingUsers}
      onSelectUser={(user) => {
        if ((user?.id ?? null) !== request.supervisor?.id) {
          updateRequestSupervisor({
            supervisor: user ?? null,
          });
        }
      }}
      search={search}
      onSearchChange={setSearch}
      tooltip={
        !request.supervisor ? (
          <p>{tooltipAddLabel}</p>
        ) : variant === 'sidebar' ? (
          // On other variants, a tooltip with the userName is displayed
          // On sidebar variant, the name is displayed on the side
          // so we can display this tooltip here
          <p>{tooltipChangeLabel}</p>
        ) : undefined
      }
    />
  );
};
